import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useId } from 'react';
import { cn } from '../cn.js';
import { Spinner } from './icons.js';
/**
 * The primary action is solid brand blue, and there is exactly one of it.
 *
 * It used to carry the brand gradient, alongside the logo mark and the rail's
 * active bar. Three different things wearing the same clothes is three things a
 * reader has to learn to tell apart, and the gradient was the loudest treatment
 * in the system being spent on the most common control in it.
 *
 * The gradient now has one job — the accent across the top of a measure card,
 * where it is decorative and carries no text. Everything pressable is flat.
 *
 * White on `--brand` is 5.17:1 and on `--brand-hover` 6.66:1, both asserted in
 * `contrast.spec.ts`. Hover darkens rather than lightens, which is the
 * direction that cannot walk a label towards illegibility.
 */
const VARIANT = {
    primary: 'bg-brand text-ink-inv border-brand hover:bg-brand-hover hover:border-brand-hover',
    secondary: 'bg-surface text-ink border-line-strong hover:bg-surface-2 hover:border-ink-3',
    ghost: 'bg-transparent text-ink-2 border-transparent hover:bg-surface-2 hover:text-ink',
    danger: 'bg-critical text-ink-inv border-critical hover:brightness-95',
};
const SIZE = {
    sm: 'min-h-row-compact px-3 text-sm',
    md: 'min-h-web-primary px-4 text-sm',
    lg: 'min-h-mobile-primary px-6 text-mobile',
};
/**
 * The button's appearance, without the button.
 *
 * For the case where the control is genuinely a link — "Open the board" goes
 * somewhere, so it is an `<a>`, and a `<button>` that navigates cannot be
 * middle-clicked, opened in a tab, or read as a destination by a screen
 * reader.
 *
 * Exported so a link can wear the button's clothes without a second copy of
 * them. The alternative is a `<Link>` with a hand-assembled class list that
 * drifts the first time the primary action changes shape.
 */
export function buttonClasses({ variant = 'primary', size = 'md', className, } = {}) {
    return cn('inline-flex items-center justify-center gap-2 rounded-md border', 'duration-fast ease-calm font-sans font-semibold transition-colors', 'disabled:cursor-not-allowed disabled:opacity-40', VARIANT[variant], SIZE[size], className);
}
export const Button = forwardRef(function Button({ variant = 'primary', size = 'md', pending, icon, disabled, disabledReason, children, className, type, ...rest }, ref) {
    const reasonId = useId();
    const hasSlot = pending !== undefined || icon !== undefined;
    const button = (_jsxs("button", { ref: ref, 
        // Never the HTML default. A button inside a form that submits it because
        // nobody typed `type` is the oldest bug in web software.
        type: type ?? 'button', disabled: disabled ?? false, "aria-busy": pending ? true : undefined, "aria-describedby": disabled ? reasonId : undefined, className: buttonClasses({ variant, size, className }), ...rest, children: [hasSlot && (
            // The slot sizes whatever it is handed. Lucide's icons default to 24px
            // with no class of their own, so `icon={<RefreshCw />}` overflowed a
            // 16px box and sat on top of the label — which is exactly the kind of
            // thing a caller should not have to know. Constraining the child here
            // means an icon cannot arrive at the wrong size.
            _jsx("span", { className: "grid h-4 w-4 flex-none place-items-center [&>svg]:h-4 [&>svg]:w-4", children: pending ? _jsx(Spinner, {}) : icon })), children] }));
    if (!disabled)
        return button;
    return (_jsxs("span", { className: "inline-flex", title: disabledReason, children: [button, _jsx("span", { id: reasonId, className: "sr-only", children: disabledReason })] }));
});
//# sourceMappingURL=button.js.map